import React, {Fragment} from 'react';
import {Box, Stack} from '@mui/material';

import {AppointmentBookingEnum} from 'main/analytics/gaTagsEnum';
import {
	AbandonFlowModal,
	AlertActions,
	AlertModal,
	MainFooter,
	MainHeader,
	StepProgressBar,
	StyledGrid,
	SwipeableEdgeDrawer,
} from 'presentation/components';
import {CustomStepper} from 'presentation/components/CustomStepper';
import {ChangeDialogsGroup} from 'presentation/modules/booking/shared/ui/components/ChangeDialogsGroup';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';
import {translations} from 'presentation/translations-new';
import {useHealthBooking} from './hooks/useHealthBooking';
import {StyledBox} from './styles';
import {HealthSummarySectionGroup} from '../../components/HealthSummarySectionGroup';
import {OurDoctorSpecialtyAlert} from 'presentation/components/OurDoctor/SpecialtyAlert';
import {HealthSearchBar} from '../../components/HealthSearchBar';
import {HealthStepsWrapper} from './steps/HealthStepsWrapper';
import {ScheduleScriptV2} from 'presentation/pages/private/booking/pages/ScheduleScriptV2';

const {abandonFlow, appointmentSummary, backFlow} = translations['pt-br'].privateFlow.bookingFlow;

export function HealthBooking() {
	const {
		getHeaderTitle,
		handleBack,
		handleClose,
		shouldHideBackBtn,
		isMobile,
		activeBookingFlowStep,
		stepLabels,
		hideStepBar,
		isLastStep,
		shouldHideSearchBarAndBookingSummary,
		customFooterMarginBottom,
		setActiveBookingFlowStep,
		abandonFlowState,
		setAbandonFlowState,
		handleConfirmExitFlow,
		showSwipeableDrawerBookingSummary,
		openDrawerState,
		setOpenDrawerState,
		handleChangeModals,
		shouldHideSearchBar,
		searchBarPlaceholder,
		handleNext,
		isScheduleScriptActive,
		mainHeaderBackButtonTagId,
		mainHeaderCloseButtonTagId,
		shouldShowOurDoctorAlert,
		searchBarTagId,
		handleBackScheduleScriptV2Confirmed,
		handleBackScheduleScriptV2Cancelled,
		openScriptV2BackModal,
		shouldUseNewResumeLayout,
		showHealthSummarySectionGroupInGrid,
	} = useHealthBooking();

	return (
		<Fragment>
			<MainHeader
				title={getHeaderTitle()}
				dataTagId={{
					backButton: mainHeaderBackButtonTagId,
					closeButton: mainHeaderCloseButtonTagId,
				}}
				handleBack={handleBack}
				handleClose={handleClose}
				hideBackBtn={shouldHideBackBtn}
				variant="subRoute">
				{hideStepBar || isScheduleScriptActive ? null : (
					<StyledGrid.Root px={isMobile ? 2 : 4} pb={3}>
						<StyledGrid.Item sm={12} md={12} lg={12}>
							<Stack gap={3}>
								{isMobile ? (
									<StepProgressBar
										mt={1}
										currentStep={activeBookingFlowStep + 1}
										totalSteps={stepLabels.length}
									/>
								) : (
									<CustomStepper
										dataTagId={AppointmentBookingEnum.NOME_STEP}
										activeStep={activeBookingFlowStep}
										stepLabels={stepLabels}
									/>
								)}
							</Stack>
						</StyledGrid.Item>
					</StyledGrid.Root>
				)}

				{shouldHideSearchBar ? null : (
					<Fragment>
						<HealthSearchBar
							activeStep={activeBookingFlowStep}
							placeholder={searchBarPlaceholder[activeBookingFlowStep]}
							searchDataTagId={searchBarTagId}
						/>
						{shouldShowOurDoctorAlert ? <OurDoctorSpecialtyAlert /> : null}
					</Fragment>
				)}
			</MainHeader>

			{isScheduleScriptActive ? (
				<ScheduleScriptV2 handleNext={handleNext} />
			) : (
				<Fragment>
					<StyledGrid.Root p={isMobile ? 2 : 4} height="100%">
						<StyledGrid.Item
							sm={12}
							md={12}
							lg={
								shouldHideSearchBarAndBookingSummary || shouldUseNewResumeLayout
									? 12
									: 8
							}>
							<HealthStepsWrapper
								activeStep={activeBookingFlowStep}
								handleBack={handleBack}
								handleNext={handleNext}
								stepLabels={stepLabels}
							/>
						</StyledGrid.Item>
						{showHealthSummarySectionGroupInGrid ? (
							<StyledGrid.Item md={4} lg={4}>
								<HealthSummarySectionGroup
									handleBack={handleBack}
									activeStep={activeBookingFlowStep}
									handleChangeModals={handleChangeModals}
									stepLabels={stepLabels}
								/>
							</StyledGrid.Item>
						) : null}
					</StyledGrid.Root>

					<StyledBox
						$isLastStep={isLastStep}
						$adjustFooterMargin={shouldHideSearchBarAndBookingSummary}>
						<MainFooter customMarginBottom={customFooterMarginBottom()} />
					</StyledBox>

					{showSwipeableDrawerBookingSummary ? (
						<SwipeableEdgeDrawer
							type="with-appbar"
							title={appointmentSummary}
							open={openDrawerState}
							setOpen={setOpenDrawerState}
							customDrawerHeight={openDrawerState ? 'auto' : undefined}>
							<Box
								paddingInline={2}
								paddingBottom={2}
								height={'100%'}
								overflow={'auto'}>
								<HealthSummarySectionGroup
									activeStep={activeBookingFlowStep}
									handleBack={handleBack}
									handleChangeModals={handleChangeModals}
									stepLabels={stepLabels}
									showTitle={false}
									disableGutters={true}
								/>
							</Box>
						</SwipeableEdgeDrawer>
					) : null}
				</Fragment>
			)}

			<AlertModal
				open={openScriptV2BackModal}
				title={backFlow.title}
				variant="info"
				headerTitle={backFlow.headerTitle}
				description={backFlow.description}
				onClose={handleBackScheduleScriptV2Cancelled}
				headerDataTagId={AppointmentBookingEnum.VISUALIZOU_MODAL_AVISO_RETORNO_SCRIPT}
				closeBtnDataTagId={AppointmentBookingEnum.CLICK_FECHAR_MODAL_AVISO_RETORNO_SCRIPT}
				footerContent={
					<AlertActions
						primaryText={backFlow.primary}
						primaryAction={handleBackScheduleScriptV2Confirmed}
						dataTagId={
							AppointmentBookingEnum.CLICK_CONFIRMAR_MODAL_AVISO_RETORNO_SCRIPT
						}
						secondaryText={backFlow.secondary}
						secondaryAction={handleBackScheduleScriptV2Cancelled}
						secondaryBtnDataTagId={
							AppointmentBookingEnum.CLICK_CANCELAR_MODAL_AVISO_RETORNO_SCRIPT
						}
					/>
				}
			/>

			<ChangeDialogsGroup
				flowType={FlowEnum.HEALTH}
				setActiveBookingFlowStep={setActiveBookingFlowStep}
			/>
			<AbandonFlowModal
				open={abandonFlowState}
				setOpen={setAbandonFlowState}
				headerTitle={abandonFlow.headerTitle}
				title={abandonFlow.title}
				description={abandonFlow.description}
				handleConfirm={handleConfirmExitFlow}
				confirmButtondataTagId={
					isScheduleScriptActive
						? AppointmentBookingEnum.ABANDONOU_SCRIPT_AGENDAMENTO
						: AppointmentBookingEnum.ABANDONOU_AGENDAMENTO
				}
				closeButtondataTagId={
					isScheduleScriptActive
						? AppointmentBookingEnum.FECHOU_MODAL_ABANDONO_SCRIPT_AGENDAMENTO
						: undefined
				}
				backButtondataTagId={
					isScheduleScriptActive
						? AppointmentBookingEnum.DESISTIU_DE_ABANDONAR_SCRIPT_AGENDAMENTO
						: undefined
				}
				modaldataTagId={AppointmentBookingEnum.VISUALIZOU_MODAL_ABANDONO}
			/>
		</Fragment>
	);
}
